import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CGUAcceptanceService } from '../services/CGUAcceptanceService';
import { useAuthenticationContext } from './AuthenticationContext';

interface CGUAcceptanceContextType {
  hasCguAccepted: boolean;
  isLoading: boolean;
  acceptCGU: () => Promise<void>;
  checkAcceptance: () => Promise<void>;
  acceptanceDate: Date | null;
  refreshCGUStatus: () => Promise<void>;
}

const CGUAcceptanceContext = createContext<CGUAcceptanceContextType | undefined>(undefined);

interface CGUAcceptanceProviderProps {
  children: ReactNode;
  cguAcceptanceService?: CGUAcceptanceService;
}

export const CGUAcceptanceProvider: React.FC<CGUAcceptanceProviderProps> = ({ 
  children, 
  cguAcceptanceService 
}) => {
  const { user, isAuthenticated, updateCGUAcceptanceUser } = useAuthenticationContext();
  const [hasCguAccepted, setHasCguAccepted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [acceptanceDate, setAcceptanceDate] = useState<Date | null>(null);

  // We'll get the service from context or dependency injection later
  // For now, we'll assume it's provided via props or we'll create it in App.tsx

  const checkAcceptance = React.useCallback(async () => {
    if (!user || !isAuthenticated || !cguAcceptanceService) {
      return;
    }

    try {
      setIsLoading(true);
      const hasAccepted = await cguAcceptanceService.hasAccepted(user);
      const date = await cguAcceptanceService.getAcceptanceDate(user);
      
      setHasCguAccepted(hasAccepted);
      setAcceptanceDate(date);
    } catch (error) {
      console.error('Error checking CGU acceptance:', error);
      // Fallback to user object data
      setHasCguAccepted(user.hasCguAccepted());
      setAcceptanceDate(user.cguAcceptedAt);
    } finally {
      setIsLoading(false);
    }
  }, [user, isAuthenticated, cguAcceptanceService]);

  const acceptCGU = async (): Promise<void> => {
    if (!user || !cguAcceptanceService || !updateCGUAcceptanceUser) {
      throw new Error('No authenticated user or service available');
    }

    try {
      const updatedUser = await cguAcceptanceService.acceptCGU(user);
      
      // Update the user in the authentication context
      await updateCGUAcceptanceUser(updatedUser);
      
      setHasCguAccepted(true);
      setAcceptanceDate(updatedUser.cguAcceptedAt);
    } catch (error) {
      console.error('Error accepting CGU:', error);
      throw new Error('Failed to accept CGU');
    }
  };

  const refreshCGUStatus = async (): Promise<void> => {
    if (!user || !cguAcceptanceService || !updateCGUAcceptanceUser) {
      return;
    }

    try {
      const refreshedUser = await cguAcceptanceService.refreshUserCGUStatus(user);
      await updateCGUAcceptanceUser(refreshedUser);
      setHasCguAccepted(refreshedUser.hasCguAccepted());
      setAcceptanceDate(refreshedUser.cguAcceptedAt);
    } catch (error) {
      console.error('Error refreshing CGU status:', error);
    }
  };

  // Update local state when user object changes (includes CGU data from Firebase)
  useEffect(() => {

    if (user && isAuthenticated) {
      // User object already contains CGU data from Firebase via authentication
      setHasCguAccepted(user.hasCguAccepted());
      setAcceptanceDate(user.cguAcceptedAt);
      setIsLoading(false);
    } else {
      setHasCguAccepted(false);
      setAcceptanceDate(null);
      setIsLoading(false);
    }
  }, [user, isAuthenticated]);

  const contextValue: CGUAcceptanceContextType = {
    hasCguAccepted,
    isLoading,
    acceptCGU,
    checkAcceptance,
    acceptanceDate,
    refreshCGUStatus
  };

  return (
    <CGUAcceptanceContext.Provider value={contextValue}>
      {children}
    </CGUAcceptanceContext.Provider>
  );
};

export const useCGUAcceptance = (): CGUAcceptanceContextType => {
  const context = useContext(CGUAcceptanceContext);
  if (context === undefined) {
    throw new Error('useCGUAcceptance must be used within a CGUAcceptanceProvider');
  }
  return context;
};